import React, { useEffect, useState } from "react";
import { useParams, Link, useNavigate } from "react-router-dom";
import { Heart, ShoppingBag, ArrowLeft, ShieldCheck } from "lucide-react";
import { api } from "../lib/api";
import { useLang } from "../contexts/LanguageContext";
import { useCart } from "../contexts/CartContext";
import { useAuth } from "../contexts/AuthContext";
import { ReptileCard } from "../components/ReptileCard";
import { toast } from "sonner";

const ReptileDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { t, lang } = useLang();
  const { addItem } = useCart();
  const { user } = useAuth();
  const [reptile, setReptile] = useState(null);
  const [related, setRelated] = useState([]);
  const [activeImg, setActiveImg] = useState(0);
  const [wishlisted, setWishlisted] = useState(false);

  useEffect(() => {
    api.get(`/reptiles/${id}`).then((r) => {
      setReptile(r.data);
      setActiveImg(0);
      api.get("/reptiles", { params: { species: r.data.species, limit: 8 } })
        .then((rr) => setRelated(rr.data.filter((x) => x.id !== r.data.id).slice(0, 4)));
    }).catch(() => navigate("/marketplace"));
  }, [id, navigate]);

  useEffect(() => {
    if (user && reptile) {
      api.get("/wishlist").then((r) => setWishlisted(r.data.some((w) => w.item_id === reptile.id)));
    }
  }, [user, reptile]);

  const handleAdd = () => {
    if (!reptile) return;
    addItem({
      item_id: reptile.id,
      item_type: "reptile",
      title: reptile.title,
      cover: reptile.cover,
      price: reptile.price,
    });
    toast.success(lang === "es" ? "Añadido al carrito" : "Added to cart");
  };

  const handleWishlist = async () => {
    if (!user) { toast.error(t("auth.signIn")); return; }
    const r = await api.post("/wishlist/toggle", { item_id: reptile.id, item_type: "reptile" });
    setWishlisted(r.data.added);
  };

  if (!reptile) {
    return <div className="min-h-screen flex items-center justify-center text-gray-500 font-mono-tech text-xs">LOADING…</div>;
  }

  const gallery = reptile.gallery && reptile.gallery.length > 0 ? reptile.gallery : [reptile.cover];

  return (
    <div className="bg-[var(--monzal-bg)] min-h-screen pb-32" data-testid="reptile-detail-page">
      <div className="max-w-[1400px] mx-auto px-5 lg:px-10 pt-10">
        <Link to="/marketplace" data-testid="back-marketplace" className="inline-flex items-center gap-2 text-xs uppercase tracking-[0.22em] font-display text-gray-400 hover:text-[var(--monzal-gold)]">
          <ArrowLeft size={14} /> {t("detail.back")}
        </Link>
      </div>

      <div className="max-w-[1400px] mx-auto px-5 lg:px-10 pt-8 grid grid-cols-1 lg:grid-cols-12 gap-10 lg:gap-14">
        {/* Gallery */}
        <div className="lg:col-span-7">
          <div className="aspect-[4/3] bg-black overflow-hidden border border-white/5 relative">
            <img
              src={gallery[activeImg]}
              alt={reptile.title}
              className="w-full h-full object-cover"
              data-testid="reptile-hero-image"
            />
            <div className="absolute top-4 left-4 flex gap-2">
              {reptile.featured && <span className="tag-gold">{t("common.featured")}</span>}
              <span className="tag-tech">{reptile.status.toUpperCase()}</span>
            </div>
          </div>
          <div className="grid grid-cols-4 gap-3 mt-3">
            {gallery.map((g, i) => (
              <button
                key={i}
                data-testid={`gallery-thumb-${i}`}
                onClick={() => setActiveImg(i)}
                className={`aspect-square overflow-hidden border ${activeImg === i ? "border-[var(--monzal-gold)]" : "border-white/5"} bg-black`}
              >
                <img src={g} alt="" className="w-full h-full object-cover opacity-80 hover:opacity-100 transition" />
              </button>
            ))}
          </div>
        </div>

        {/* Info */}
        <div className="lg:col-span-5">
          <div className="overline" data-testid="reptile-species">{reptile.species}</div>
          <h1 className="font-display font-light text-3xl lg:text-5xl text-white tracking-tight mt-3" data-testid="reptile-title">
            {reptile.title}
          </h1>

          <Link to={`/breeder/${reptile.breeder_id.replace('brd_','')}`} className="inline-flex items-center gap-2 mt-5 text-sm text-gray-300 hover:text-[var(--monzal-gold)]">
            <ShieldCheck size={14} className="text-[var(--monzal-emerald)]" />
            <span className="overline text-[var(--monzal-text-2)]">{t("detail.breederBy")}</span>
            <span className="font-display">{reptile.breeder_name}</span>
          </Link>

          <div className="mt-8 text-4xl font-display font-light text-[var(--monzal-gold)]" data-testid="reptile-price">
            ${reptile.price.toLocaleString()}
            <span className="text-sm text-gray-500 ml-2 font-mono-tech">{reptile.currency}</span>
          </div>

          {/* Genetics grid */}
          <div className="mt-10 grid grid-cols-2 gap-px bg-white/5 border border-white/5">
            <Stat label={t("detail.species")} value={reptile.species} />
            <Stat label={t("detail.sex")} value={reptile.sex.toUpperCase()} />
            <Stat label={t("detail.weight")} value={`${reptile.weight_g} g`} />
            <Stat label={t("detail.age")} value={`${Math.round(reptile.age_months)} ${t("detail.months")}`} />
            <Stat label={t("detail.hatch")} value={reptile.hatch_date} />
            <Stat label={t("detail.feeding")} value={reptile.feeding_status} />
          </div>

          {/* Genetics */}
          <div className="mt-8">
            <div className="overline mb-3">{t("detail.genetics")}</div>
            <div className="flex flex-wrap gap-2" data-testid="reptile-genetics">
              {reptile.genetics.map((g) => <span key={g} className="tag-gold">{g}</span>)}
              {reptile.genetics.length === 0 && <span className="text-gray-500 text-xs font-mono-tech">—</span>}
            </div>
          </div>

          {reptile.hets.length > 0 && (
            <div className="mt-6">
              <div className="overline mb-3">{t("detail.hets")}</div>
              <div className="flex flex-wrap gap-2" data-testid="reptile-hets">
                {reptile.hets.map((h) => <span key={h} className="tag-tech">het {h}</span>)}
              </div>
            </div>
          )}

          {/* Breeder notes */}
          <div className="mt-8 border-t border-white/5 pt-6">
            <div className="overline mb-3">{t("detail.breederNotes")}</div>
            <p className="text-gray-300 text-sm leading-relaxed">{reptile.breeder_notes}</p>
          </div>
        </div>
      </div>

      {/* Related */}
      {related.length > 0 && (
        <div className="max-w-[1400px] mx-auto px-5 lg:px-10 mt-24">
          <div className="overline mb-6">{t("detail.relatedTitle")}</div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {related.map((r) => <ReptileCard key={r.id} reptile={r} testIdPrefix="related" />)}
          </div>
        </div>
      )}

      {/* Sticky buy bar */}
      <div className="fixed bottom-0 left-0 right-0 z-40 bg-black/85 backdrop-blur-xl border-t border-white/5">
        <div className="max-w-[1400px] mx-auto px-5 lg:px-10 py-4 flex items-center justify-between gap-4">
          <div className="hidden sm:block">
            <div className="text-xs text-gray-500 font-mono-tech uppercase tracking-wider">{reptile.morph}</div>
            <div className="font-display text-[var(--monzal-gold)] text-lg">${reptile.price.toLocaleString()}</div>
          </div>
          <div className="flex items-center gap-3 ml-auto">
            <button data-testid="reptile-wishlist-btn" onClick={handleWishlist} className={`btn-ghost ${wishlisted ? "border-[var(--monzal-gold)] text-[var(--monzal-gold)]" : ""}`}>
              <Heart size={14} fill={wishlisted ? "currentColor" : "none"} />
            </button>
            <button data-testid="add-to-cart-btn" onClick={handleAdd} className="btn-emerald-glow">
              <ShoppingBag size={14} /> {t("detail.addToCart")}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

const Stat = ({ label, value }) => (
  <div className="bg-[var(--monzal-surface)] p-4">
    <div className="text-[10px] uppercase tracking-[0.25em] font-display text-gray-500 mb-2">{label}</div>
    <div className="font-mono-tech text-sm text-white">{value}</div>
  </div>
);

export default ReptileDetail;
